import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  MessageCircle, Search, X, Share2, Check, Loader2, ArrowUpDown, ShoppingCart,
  Plus, Minus, Trash2, ImageOff, Clock, MapPin, Sparkles,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";

const PAGE_SIZE = 24;
const LUX = "catalogue-lux"; // apply to portalled Shadcn content so tokens/fonts reach them
const HERO_IMG =
  "https://images.unsplash.com/photo-1523059623039-a9ed027e7fad?q=80&w=1600&auto=format&fit=crop";

// Enable scroll-reveal only when supported + user hasn't asked for reduced motion (keeps it lightweight/accessible)
const MOTION_OK = typeof window !== "undefined"
  && "IntersectionObserver" in window
  && !(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

// Persistent per-browser id for analytics (unique visitor counting)
const VISITOR_KEY = "jastip_visitor_id";
function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `v_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return null;
  }
}

const SORTS = [
  { value: "terbaru", label: "Terbaru" },
  { value: "terlama", label: "Terlama" },
  { value: "termurah", label: "Harga terendah" },
  { value: "termahal", label: "Harga tertinggi" },
];

const PROMO_TAGS = [
  { value: "diskon", label: "Diskon" },
  { value: "Terlaris", label: "Terlaris" },
  { value: "Baru", label: "Baru" },
  { value: "Promo", label: "Promo" },
  { value: "Stok Terbatas", label: "Stok Terbatas" },
];

const LABEL_STYLE = {
  Terlaris: "bg-[hsl(var(--lux-surface))]/90 text-[hsl(var(--lux-gold))] border-[hsl(var(--lux-hairline))]",
  Baru: "bg-[hsl(var(--lux-surface))]/90 text-[hsl(var(--primary))] border-[hsl(var(--lux-hairline))]",
  Promo: "bg-[hsl(var(--lux-surface))]/90 text-[hsl(var(--primary))] border-[hsl(var(--lux-hairline))]",
  "Stok Terbatas": "bg-[hsl(var(--warning-bg))]/95 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
};

function useIsDesktop() {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const h = () => setD(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return d;
}

function closingInfo(dateStr) {
  if (!dateStr) return null;
  const end = new Date(dateStr);
  if (isNaN(end.getTime())) return null;
  const t = new Date();
  const d0 = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  const d1 = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const days = Math.round((d1 - d0) / 86400000);
  if (days < 0) return { label: "Order sudah ditutup", tone: "closed" };
  if (days === 0) return { label: "Tutup order hari ini", tone: "urgent" };
  if (days === 1) return { label: "Tutup order besok", tone: "urgent" };
  return { label: `Tutup order dalam ${days} hari`, tone: days <= 3 ? "urgent" : "normal" };
}

function LabelPill({ label }) {
  const cls = LABEL_STYLE[label] || "bg-[hsl(var(--lux-surface))]/90 text-[hsl(var(--primary))] border-[hsl(var(--lux-hairline))]";
  return (
    <span data-testid="product-label-badge"
      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium leading-none backdrop-blur-sm shadow-sm ${cls}`}>
      {label}
    </span>
  );
}

// Strict badge slots: discount top-left, marketing labels stacked top-right (max 2 + "+N").
function CardBadges({ item }) {
  const labels = (item.labels || []).filter(Boolean);
  const shown = labels.slice(0, 2);
  const extra = labels.length - shown.length;
  const hasDiscount = item.discount_active && item.discount_percent > 0;
  return (
    <>
      {hasDiscount && (
        <span data-testid="catalogue-discount-badge"
          className="absolute left-3 top-3 z-10 rounded-full bg-[hsl(var(--lux-ink))] px-2.5 py-1 text-[11px] font-semibold leading-none text-[hsl(var(--background))] shadow-sm [font-variant-numeric:tabular-nums]">
          -{item.discount_percent}%
        </span>
      )}
      {shown.length > 0 && (
        <div className="absolute right-3 top-3 z-10 flex flex-col items-end gap-1.5">
          {shown.map((l) => <LabelPill key={l} label={l} />)}
          {extra > 0 && (
            <span className="rounded-full border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--lux-surface-2))] px-2 py-0.5 text-[11px] font-medium leading-none text-[hsl(var(--lux-ink))]">
              +{extra}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function LuxImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt={alt} loading="lazy" className={className} onError={() => setFailed(true)} />;
  }
  return (
    <div className="lux-grain flex h-full w-full flex-col items-center justify-center gap-2 bg-[hsl(var(--lux-surface))] text-[hsl(var(--muted-foreground))]">
      <LogoMark className="h-10 w-10 opacity-25" />
      <span className="flex items-center gap-1 text-[11px] font-medium tracking-wide">
        <ImageOff className="h-3.5 w-3.5" />Foto menyusul
      </span>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-2xl border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]">
          <Skeleton className="aspect-[4/5] w-full rounded-none" />
          <div className="space-y-2 p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="mt-3 h-5 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyCatalogue({ title, subtitle, onReset }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/60 px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--lux-surface))] text-[hsl(var(--primary))]">
        <Search className="h-6 w-6" />
      </div>
      <h3 className="font-heading text-xl font-medium text-[hsl(var(--lux-ink))]">{title}</h3>
      {subtitle && <p className="mt-2 max-w-sm text-sm text-[hsl(var(--muted-foreground))]">{subtitle}</p>}
      {onReset && (
        <Button variant="outline" className="mt-5 rounded-xl border-[hsl(var(--lux-hairline))]" onClick={onReset} data-testid="catalogue-reset-filter">
          Hapus semua filter
        </Button>
      )}
    </div>
  );
}

export default function PublicCatalogue() {
  const { tripId } = useParams();
  const isDesktop = useIsDesktop();

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState("terbaru");
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [buyer, setBuyer] = useState({ name: "", wa: "", address: "", note: "" });
  const [restoredCart, setRestoredCart] = useState(false);

  const cartKey = tripId ? `jastip_cart_${tripId}` : null;
  const buyerKey = tripId ? `jastip_buyer_${tripId}` : null;

  const cartList = Object.values(cart);
  const cartCount = cartList.reduce((n, c) => n + c.qty, 0);
  const cartTotal = cartList.reduce((n, c) => n + (c.item.final_price || 0) * c.qty, 0);
  const anyFilter = !!(category || tag || debouncedSearch);

  const addToCart = (item, qty = 1, silent = false) => {
    setCart((prev) => {
      const cur = prev[item.id];
      return { ...prev, [item.id]: { item, qty: (cur ? cur.qty : 0) + qty } };
    });
    if (!silent) toast.success("Ditambahkan ke keranjang", { description: item.name });
  };
  const setQty = (id, qty) => setCart((prev) => {
    if (qty <= 0) { const { [id]: _drop, ...rest } = prev; return rest; }
    return { ...prev, [id]: { ...prev[id], qty } };
  });
  const resetFilters = () => { setCategory(""); setTag(""); setSearch(""); };

  // ---- Analytics tracking (fire-and-forget; never blocks UI) ----
  const track = useCallback((event_type, extra = {}) => {
    if (!tripId) return;
    try {
      api.post("/analytics/track", {
        event_type,
        trip_ref: tripId,
        visitor_id: getVisitorId(),
        path: typeof window !== "undefined" ? window.location.pathname : undefined,
        referrer: typeof document !== "undefined" ? (document.referrer || undefined) : undefined,
        ...extra,
      }).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }, [tripId]);

  const viewedRef = useRef(false);

  const openProduct = useCallback((p) => {
    setSelected(p);
    track("product_click", { product_id: p.id, product_name: p.name });
  }, [track]);

  // ---- Cart persistence (per trip) ----
  useEffect(() => {
    if (!cartKey) return;
    try {
      const raw = localStorage.getItem(cartKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && Object.keys(parsed).length) {
          setCart(parsed);
          setRestoredCart(true);
        }
      }
      const b = buyerKey && localStorage.getItem(buyerKey);
      if (b) setBuyer((prev) => ({ ...prev, ...JSON.parse(b) }));
    } catch { /* ignore corrupt storage */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey]);

  useEffect(() => {
    if (restoredCart && meta) {
      toast("Keranjang dipulihkan", { description: "Pesanan yang belum selesai kami simpan untukmu." });
      setRestoredCart(false);
    }
  }, [restoredCart, meta]);

  useEffect(() => {
    if (!cartKey) return;
    try {
      if (Object.keys(cart).length) localStorage.setItem(cartKey, JSON.stringify(cart));
      else localStorage.removeItem(cartKey);
    } catch { /* quota / private mode */ }
  }, [cart, cartKey]);

  useEffect(() => {
    if (!buyerKey) return;
    try { localStorage.setItem(buyerKey, JSON.stringify(buyer)); } catch { /* ignore */ }
  }, [buyer, buyerKey]);

  // Debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async (pageNum, replace) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const { data } = await api.get(`/public/catalogue/${tripId}`, {
        params: {
          search: debouncedSearch || undefined, category: category || undefined,
          tag: tag || undefined, sort, page: pageNum, page_size: PAGE_SIZE,
        },
      });
      const { items: pageItems = [], ...rest } = data;
      setMeta(rest);
      setItems((prev) => (replace ? pageItems : [...prev, ...pageItems]));
      setHasMore(!!rest.has_more);
      setPage(pageNum);
    } catch {
      if (replace) { setMeta(null); setItems([]); setHasMore(false); }
    } finally {
      if (replace) setLoading(false); else setLoadingMore(false);
    }
  }, [tripId, debouncedSearch, category, tag, sort]);

  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  useEffect(() => {
    if (meta) document.title = `${meta.catalogue_title || meta.business_name || "Katalog"} | Jastip`;
  }, [meta]);

  // Record one page_view per catalogue load (once the catalogue is confirmed to exist)
  useEffect(() => {
    if (meta && !viewedRef.current) {
      viewedRef.current = true;
      track("page_view");
    }
  }, [meta, track]);

  // Reveal-on-scroll: single IntersectionObserver, transform/opacity only (no library)
  useEffect(() => {
    if (!MOTION_OK || loading) return;
    const nodes = document.querySelectorAll(".catalogue-lux.motion-on .reveal:not(.is-visible)");
    if (!nodes.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -6% 0px", threshold: 0.06 });
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [items, loading]);

  const waLink = (item) => {
    const num = (meta.whatsapp_number || "").replace(/[^0-9]/g, "");
    const tmpl = meta.whatsapp_message_template || "Halo, saya ingin memesan [Product Name] untuk trip [Trip Name].";
    const msg = tmpl
      .replaceAll("[Product Name]", item.name || "")
      .replaceAll("[Trip Name]", meta.trip.name || "")
      .replaceAll("[Variant]", item.variant || "-")
      .replaceAll("[Final Price]", formatIDR(item.final_price))
      .replaceAll("[Quantity]", "1")
      .replaceAll("[Product URL]", item.product_url || "");
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const shareCatalogue = async () => {
    const url = window.location.href;
    const title = (meta && (meta.catalogue_title || meta.business_name)) || "Katalog Jastip";
    try {
      if (navigator.share) {
        await navigator.share({ title, text: `Lihat katalog: ${title}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch { /* user cancelled */ }
  };

  const checkoutWhatsApp = () => {
    if (!cartList.length) return;
    const num = (meta.whatsapp_number || "").replace(/[^0-9]/g, "");
    const L = [];
    L.push(`*${meta.business_name}* — Pesanan Katalog`);
    L.push(`Trip: ${meta.trip.name}`);
    L.push("");
    L.push("*Pesanan saya:*");
    cartList.forEach((c, i) => {
      const nm = c.item.name + (c.item.variant ? ` (${c.item.variant})` : "");
      L.push(`${i + 1}. ${nm}`);
      L.push(`   ${c.qty} x ${formatIDR(c.item.final_price)} = ${formatIDR(c.item.final_price * c.qty)}`);
    });
    L.push("");
    L.push(`*Total: ${formatIDR(cartTotal)}* (${cartCount} item)`);
    L.push("");
    L.push("*Data pemesan:*");
    L.push(`Nama: ${buyer.name || "-"}`);
    if (buyer.wa) L.push(`No. WA: ${buyer.wa}`);
    if (buyer.address) L.push(`Alamat: ${buyer.address}`);
    if (buyer.note) L.push(`Catatan: ${buyer.note}`);
    L.push("");
    L.push("Terima kasih!");
    const base = num ? `https://wa.me/${num}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(L.join("\n"))}`, "_blank");
  };

  // ---- Initial full-page skeleton ----
  if (loading && !meta) {
    return (
      <div className={`${LUX} catalogue-lux-bg min-h-screen`}>
        <div className="border-b border-[hsl(var(--lux-hairline))]">
          <div className="mx-auto max-w-7xl space-y-3 px-4 py-10 sm:px-6 lg:px-10">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-4 w-80" />
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10"><SkeletonGrid /></div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className={`${LUX} catalogue-lux-bg flex min-h-screen items-center justify-center px-4`}>
        <EmptyCatalogue title="Katalog tidak ditemukan" subtitle="Tautan mungkin salah atau katalog sudah tidak aktif." />
      </div>
    );
  }

  const hasCats = meta.categories && meta.categories.length > 0;
  const catList = [
    { name: "", label: "Semua", count: meta.total_products },
    ...(meta.categories || []).map((c) => ({ name: c.name, label: c.name, count: c.count })),
  ];
  const closing = closingInfo(meta.trip.order_closing_date);
  const heroOpacity = typeof meta.hero_opacity === "number" ? Math.max(0.1, Math.min(1, meta.hero_opacity)) : 0.9;
  const closingCls = closing
    ? (closing.tone === "closed"
        ? "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--lux-hairline))]"
        : closing.tone === "urgent"
        ? "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"
        : "bg-[hsl(var(--lux-surface-2))] text-[hsl(var(--primary))] border-[hsl(var(--lux-hairline))]")
    : "";

  const chip = (active) =>
    `shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium h-10 transition-colors ${
      active
        ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
        : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border-[hsl(var(--lux-hairline))] hover:bg-[hsl(var(--secondary))]"
    }`;

  const DetailBody = ({ item, onClose, showClose }) => {
    const meta2 = [item.brand, item.variant, item.colour, item.size].filter(Boolean);
    return (
      <div className="relative grid md:grid-cols-2">
        {showClose && (
          <button onClick={onClose} data-testid="catalogue-detail-close" aria-label="Tutup"
            className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/85 backdrop-blur transition-colors hover:bg-[hsl(var(--secondary))]">
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-[hsl(var(--lux-surface))] md:aspect-auto md:min-h-[460px]">
          <LuxImage src={item.photo} alt={item.name} className="h-full w-full object-cover" />
          <CardBadges item={item} />
        </div>
        <div className="flex flex-col overflow-y-auto p-6 sm:p-8 md:max-h-[82vh]">
          <div className="flex flex-wrap items-center gap-2">
            {item.category && (
              <span className="rounded-full bg-[hsl(var(--lux-surface))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--primary))]">{item.category}</span>
            )}
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${item.availability === "Tersedia" ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>
              {item.availability}
            </span>
          </div>
          <h2 className="mt-3 font-heading text-2xl font-medium leading-snug tracking-[-0.01em] text-[hsl(var(--lux-ink))] sm:text-3xl" data-testid="catalogue-detail-name">
            {item.name}
          </h2>
          {meta2.length > 0 && (
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{meta2.join(" · ")}</p>
          )}
          <div className="mt-4 flex flex-wrap items-baseline gap-3">
            <span className="font-heading text-3xl font-semibold text-[hsl(var(--primary))] [font-variant-numeric:tabular-nums]">{formatIDR(item.final_price)}</span>
            {item.discount_active && item.discount_percent > 0 && (<>
              <span className="text-base text-[hsl(var(--muted-foreground))] line-through [font-variant-numeric:tabular-nums]" data-testid="catalogue-detail-original-price">{formatIDR(item.original_price)}</span>
              <span className="rounded-full bg-[hsl(var(--lux-ink))] px-2 py-1 text-xs font-semibold text-[hsl(var(--background))]" data-testid="catalogue-detail-discount-badge">-{item.discount_percent}%</span>
            </>)}
          </div>
          {item.description
            ? <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-[hsl(var(--foreground))]/85" data-testid="catalogue-detail-description">{item.description}</p>
            : <p className="mt-5 text-sm italic text-[hsl(var(--muted-foreground))]">Belum ada deskripsi produk.</p>}

          <div className="mt-auto pt-6">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => { addToCart(item, 1, true); onClose(); setCartOpen(true); }} data-testid="catalogue-detail-add-cart"
                className="h-12 flex-1 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/92 active:scale-[0.99]">
                <ShoppingCart className="mr-2 h-5 w-5" />Tambah ke keranjang
              </Button>
              <a href={waLink(item)} target="_blank" rel="noreferrer" data-testid="catalogue-detail-whatsapp-button"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[hsl(var(--whatsapp))] px-5 font-medium text-[hsl(var(--whatsapp-foreground))] transition-colors hover:bg-[hsl(var(--whatsapp))]/90">
                <MessageCircle className="h-5 w-5" />Pesan 1
              </a>
            </div>
            {showClose && (
              <button onClick={onClose} data-testid="catalogue-detail-close-button"
                className="mt-2 h-11 w-full rounded-xl border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))] text-sm font-medium transition-colors hover:bg-[hsl(var(--secondary))]">
                Tutup
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`${LUX} ${MOTION_OK ? "motion-on" : ""} catalogue-lux-bg min-h-screen pb-16`}>
      {/* ---------------- Hero ---------------- */}
      <header className="lux-grain relative overflow-hidden border-b border-[hsl(var(--lux-hairline))]">
        {/* soft image layer (muted so text stays readable) */}
        <div aria-hidden className="absolute inset-0">
          <img src={meta.hero_image || HERO_IMG} alt="" draggable={false}
            className="h-full w-full object-cover object-center"
            style={{ opacity: heroOpacity, filter: "saturate(0.85)" }}
            onError={(e) => { if (e.currentTarget.src !== HERO_IMG) e.currentTarget.src = HERO_IMG; }} />
        </div>
        {/* legibility wash: strong ivory from the left (where text sits) + gentle top/bottom fade */}
        <div aria-hidden className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(96deg, hsla(38,50%,96%,0.98) 0%, hsla(38,50%,96%,0.94) 26%, hsla(38,50%,96%,0.66) 52%, hsla(38,50%,96%,0.34) 80%, hsla(38,50%,96%,0.22) 100%), linear-gradient(180deg, hsla(38,50%,96%,0.28) 0%, hsla(38,50%,96%,0.02) 46%, hsla(38,50%,96%,0.42) 100%)",
          }} />

        <div className="relative mx-auto flex min-h-[320px] max-w-7xl flex-col justify-between gap-8 px-4 py-6 sm:min-h-[380px] sm:px-6 lg:min-h-[420px] lg:px-10">
          {/* top row */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-full border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/75 px-3 py-1.5 backdrop-blur-md">
              <LogoMark className="h-7 w-7" />
              <span className="font-heading text-sm font-semibold tracking-tight text-[hsl(var(--lux-ink))]">{meta.business_name}</span>
            </div>
            <button onClick={shareCatalogue} data-testid="catalogue-share-button" aria-label="Bagikan katalog"
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-full border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/75 px-4 text-sm font-medium backdrop-blur-md transition-colors hover:bg-[hsl(var(--card))]">
              {copied
                ? <><Check className="h-4 w-4 text-[hsl(var(--primary))]" /><span className="hidden sm:inline">Tersalin</span></>
                : <><Share2 className="h-4 w-4" /><span className="hidden sm:inline">Bagikan</span></>}
            </button>
          </div>

          {/* title block */}
          <div className="max-w-3xl reveal">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--accent-foreground))]/70">Katalog Jastip</span>
            <h1 className="mt-2 font-heading text-4xl font-medium leading-[1.05] tracking-[-0.02em] text-[hsl(var(--lux-ink))] sm:text-5xl lg:text-6xl">
              {meta.catalogue_title || meta.trip.name}
            </h1>
            {meta.catalogue_description && (
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[hsl(var(--muted-foreground))] sm:text-base">{meta.catalogue_description}</p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/80 px-3 py-1.5 text-sm font-medium text-[hsl(var(--foreground))] backdrop-blur-sm">
                <MapPin className="h-4 w-4 text-[hsl(var(--primary))]" />
                {meta.trip.australia_location} <span className="text-[hsl(var(--muted-foreground))]">→</span> {meta.trip.indonesia_destination}
              </span>
              {closing && (
                <span data-testid="catalogue-countdown-badge"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${closingCls}`}>
                  <Clock className="h-4 w-4" />{closing.label}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ---------------- Sticky search + sort ---------------- */}
      <div className="sticky top-0 z-30 border-b border-[hsl(var(--lux-hairline))] bg-[hsl(var(--background))]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center gap-2.5 px-4 py-3 sm:px-6 lg:px-10">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input className="h-11 rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))] pl-10 pr-9"
              placeholder="Cari produk, brand, varian…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalogue-search" />
            {search && (
              <button onClick={() => setSearch("")} aria-label="Bersihkan pencarian" data-testid="catalogue-search-clear"
                className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-[150px] shrink-0 rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))] sm:w-52" data-testid="catalogue-sort-select">
              <ArrowUpDown className="mr-1.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" /><SelectValue />
            </SelectTrigger>
            <SelectContent className={LUX}>
              {SORTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`catalogue-sort-${s.value}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ---------------- Body ---------------- */}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
        {/* Mobile filter chips */}
        <div className="lg:hidden">
          {hasCats && (
            <div className="-mx-4 mb-3 overflow-x-auto px-4 no-scrollbar" data-testid="catalogue-category-chips">
              <div className="flex w-max gap-2 pb-1">
                {catList.map((c) => {
                  const active = category === c.name;
                  return (
                    <button key={c.name || "all"} onClick={() => setCategory(c.name)} data-testid="catalogue-category-chip" className={chip(active)}>
                      {c.label}<span className={`text-xs ${active ? "opacity-80" : "text-[hsl(var(--muted-foreground))]"}`}>{c.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="-mx-4 mb-5 overflow-x-auto px-4 no-scrollbar" data-testid="catalogue-promo-chips">
            <div className="flex w-max gap-2 pb-1">
              {PROMO_TAGS.map((p) => {
                const active = tag === p.value;
                return (
                  <button key={p.value} onClick={() => setTag(active ? "" : p.value)} data-testid="catalogue-promo-chip" className={chip(active)}>
                    <Sparkles className="h-3.5 w-3.5" />{p.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Desktop filter rail */}
          <aside className="hidden lg:col-span-3 lg:block" data-testid="catalogue-category-sidebar">
            <div className="sticky top-20 space-y-6 rounded-2xl border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/70 p-5">
              {hasCats && (
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Kategori</p>
                  <div className="space-y-1">
                    {catList.map((c) => {
                      const active = category === c.name;
                      return (
                        <button key={c.name || "all"} onClick={() => setCategory(c.name)} data-testid="catalogue-category-item"
                          className={`flex h-10 w-full items-center justify-between gap-2 rounded-lg px-3 text-sm transition-colors ${
                            active ? "bg-[hsl(var(--primary))] font-medium text-[hsl(var(--primary-foreground))]" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]"
                          }`}>
                          <span className="truncate">{c.label}</span>
                          <span className={`text-xs [font-variant-numeric:tabular-nums] ${active ? "opacity-80" : "text-[hsl(var(--muted-foreground))]"}`}>{c.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Promo & Label</p>
                <div className="flex flex-wrap gap-2">
                  {PROMO_TAGS.map((p) => {
                    const active = tag === p.value;
                    return (
                      <button key={p.value} onClick={() => setTag(active ? "" : p.value)} data-testid="catalogue-promo-chip-side"
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 text-sm font-medium h-9 transition-colors ${
                          active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                                 : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border-[hsl(var(--lux-hairline))] hover:bg-[hsl(var(--secondary))]"
                        }`}>
                        <Sparkles className="h-3.5 w-3.5" />{p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {anyFilter && (
                <button onClick={resetFilters} data-testid="catalogue-reset-filter-side"
                  className="text-sm font-medium text-[hsl(var(--primary))] hover:underline">Hapus semua filter</button>
              )}
            </div>
          </aside>

          {/* Main content */}
          <main className="min-w-0 lg:col-span-9">
            {loading ? (
              <SkeletonGrid />
            ) : items.length === 0 ? (
              <EmptyCatalogue
                title="Tidak ada produk yang cocok"
                subtitle={anyFilter ? "Coba hapus filter atau ganti kata kunci pencarian." : "Produk akan segera hadir di sini."}
                onReset={anyFilter ? resetFilters : undefined}
              />
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="catalogue-result-count">
                    Menampilkan <span className="font-medium text-[hsl(var(--foreground))]">{items.length}</span> dari {meta.total_filtered} produk
                  </p>
                  {anyFilter && (
                    <button onClick={resetFilters} data-testid="catalogue-reset-filter"
                      className="text-sm font-medium text-[hsl(var(--primary))] hover:underline">Hapus filter</button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
                  {items.map((p) => {
                    const img = p.thumb || p.photo;
                    const cardMeta = [p.brand, p.variant].filter(Boolean).join(" · ");
                    return (
                      <article key={p.id} data-testid="catalogue-product-card"
                        className="group reveal flex flex-col overflow-hidden rounded-2xl border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))] shadow-[0_10px_30px_-18px_rgba(17,24,39,0.25)] transition-transform duration-200 hover:-translate-y-1">
                        <button onClick={() => openProduct(p)} aria-label={`Lihat ${p.name}`}
                          className="relative block overflow-hidden rounded-t-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]">
                          <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--lux-surface))]">
                            <LuxImage src={img} alt={p.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                            <CardBadges item={p} />
                          </div>
                        </button>
                        <div className="flex flex-1 flex-col p-4 sm:p-5">
                          <button onClick={() => openProduct(p)} className="text-left">
                            <h3 className="line-clamp-2 font-heading text-base font-medium leading-snug text-[hsl(var(--lux-ink))] sm:text-lg">{p.name}</h3>
                            {p.description
                              ? <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[hsl(var(--muted-foreground))]" data-testid="catalogue-card-description">{p.description}</p>
                              : <p className="mt-1 line-clamp-1 text-xs text-[hsl(var(--muted-foreground))]">{cardMeta || "\u00a0"}</p>}
                          </button>
                          <div className="mt-4 flex items-end justify-between gap-2">
                            <div className="flex min-w-0 flex-col">
                              <span className="font-heading text-base font-bold text-[hsl(var(--primary))] [font-variant-numeric:tabular-nums] sm:text-lg">{formatIDR(p.final_price)}</span>
                              {p.discount_active && p.discount_percent > 0 && (
                                <span className="text-[11px] leading-tight text-[hsl(var(--muted-foreground))] line-through [font-variant-numeric:tabular-nums]" data-testid="catalogue-card-original-price">{formatIDR(p.original_price)}</span>
                              )}
                            </div>
                            <button onClick={() => addToCart(p)} data-testid="catalogue-add-cart" title="Tambah ke keranjang" aria-label="Tambah ke keranjang"
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] transition-transform hover:bg-[hsl(var(--primary))]/92 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]">
                              <Plus className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {hasMore && (
                  <div className="mt-10 flex justify-center">
                    <Button variant="outline" onClick={() => fetchPage(page + 1, false)} disabled={loadingMore} data-testid="catalogue-load-more"
                      className="h-11 rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))] px-6">
                      {loadingMore ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Memuat…</> : "Muat lebih banyak"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* ---------------- Product detail ---------------- */}
      <Dialog open={!!selected && isDesktop} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className={`${LUX} max-w-3xl gap-0 overflow-hidden p-0`} data-testid="catalogue-detail-dialog">
          {selected && <DialogTitle className="sr-only">{selected.name}</DialogTitle>}
          {selected && <DetailBody item={selected} onClose={() => setSelected(null)} showClose={false} />}
        </DialogContent>
      </Dialog>
      <Drawer open={!!selected && !isDesktop} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DrawerContent className={LUX} data-testid="catalogue-detail-drawer">
          {selected && <DrawerTitle className="sr-only">{selected.name}</DrawerTitle>}
          <div className="max-h-[86vh] overflow-y-auto">
            {selected && <DetailBody item={selected} onClose={() => setSelected(null)} showClose={true} />}
          </div>
        </DrawerContent>
      </Drawer>

      {/* ---------------- Floating cart ---------------- */}
      {cartCount > 0 && (
        <button onClick={() => setCartOpen(true)} data-testid="catalogue-cart-fab"
          className="fixed bottom-5 right-5 z-40 flex h-14 items-center gap-2.5 rounded-full border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/85 pl-4 pr-5 shadow-[0_18px_50px_-22px_rgba(17,24,39,0.5)] backdrop-blur-md transition-transform hover:-translate-y-0.5 active:scale-[0.98]">
          <div className="relative">
            <ShoppingCart className="h-5 w-5 text-[hsl(var(--primary))]" />
            <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1 text-[10px] font-bold text-[hsl(var(--primary-foreground))]" data-testid="catalogue-cart-count">{cartCount}</span>
          </div>
          <span className="font-heading text-sm font-semibold text-[hsl(var(--lux-ink))] [font-variant-numeric:tabular-nums]">{formatIDR(cartTotal)}</span>
        </button>
      )}

      {/* ---------------- Cart + order form ---------------- */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right" className={`${LUX} flex w-full flex-col p-0 sm:max-w-md`} data-testid="catalogue-cart-sheet">
          <SheetHeader className="border-b border-[hsl(var(--lux-hairline))] px-5 py-4">
            <SheetTitle className="flex items-center gap-2 font-heading text-lg">
              <ShoppingCart className="h-5 w-5 text-[hsl(var(--primary))]" />Keranjang ({cartCount})
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {cartList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--lux-surface))] text-[hsl(var(--primary))]"><ShoppingCart className="h-5 w-5" /></div>
                <p className="font-heading text-base font-medium text-[hsl(var(--lux-ink))]">Keranjang kosong</p>
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Tambahkan produk dari katalog.</p>
              </div>
            ) : cartList.map(({ item, qty }) => (
              <div key={item.id} className="flex gap-3 rounded-xl border border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))] p-2.5" data-testid="cart-item">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[hsl(var(--lux-surface))]">
                  <LuxImage src={item.thumb || item.photo} alt={item.name} className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-medium leading-snug text-[hsl(var(--lux-ink))]">{item.name}</p>
                  <p className="mt-0.5 text-sm font-semibold text-[hsl(var(--primary))] [font-variant-numeric:tabular-nums]">{formatIDR(item.final_price)}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <button onClick={() => setQty(item.id, qty - 1)} data-testid="cart-item-dec" aria-label="Kurangi"
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-[hsl(var(--lux-hairline))] hover:bg-[hsl(var(--secondary))]"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-7 text-center text-sm font-semibold [font-variant-numeric:tabular-nums]" data-testid="cart-item-qty">{qty}</span>
                    <button onClick={() => setQty(item.id, qty + 1)} data-testid="cart-item-inc" aria-label="Tambah"
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-[hsl(var(--lux-hairline))] hover:bg-[hsl(var(--secondary))]"><Plus className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setQty(item.id, 0)} data-testid="cart-item-remove" aria-label="Hapus"
                      className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg border border-[hsl(var(--lux-hairline))] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}

            {cartList.length > 0 && (
              <div className="space-y-3 pt-3">
                <p className="font-heading text-sm font-medium text-[hsl(var(--lux-ink))]">Data pemesan</p>
                <Input className="h-11 rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]" placeholder="Nama *" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid="cart-buyer-name" />
                <Input className="h-11 rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]" placeholder="No. WhatsApp (opsional)" value={buyer.wa} onChange={(e) => setBuyer({ ...buyer, wa: e.target.value })} data-testid="cart-buyer-wa" />
                <Input className="h-11 rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]" placeholder="Alamat / Kota (opsional)" value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} data-testid="cart-buyer-address" />
                <Textarea className="rounded-xl border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]" placeholder="Catatan (opsional)" rows={2} value={buyer.note} onChange={(e) => setBuyer({ ...buyer, note: e.target.value })} data-testid="cart-buyer-note" />
              </div>
            )}
          </div>

          {cartList.length > 0 && (
            <div className="space-y-3 border-t border-[hsl(var(--lux-hairline))] bg-[hsl(var(--card))]/60 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[hsl(var(--muted-foreground))]">Total ({cartCount} item)</span>
                <span className="font-heading text-xl font-semibold text-[hsl(var(--primary))] [font-variant-numeric:tabular-nums]" data-testid="cart-total">{formatIDR(cartTotal)}</span>
              </div>
              <button onClick={checkoutWhatsApp} disabled={!buyer.name.trim()} data-testid="cart-checkout-wa"
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[hsl(var(--whatsapp))] font-medium text-[hsl(var(--whatsapp-foreground))] transition-colors hover:bg-[hsl(var(--whatsapp))]/90 disabled:cursor-not-allowed disabled:opacity-50">
                <MessageCircle className="h-5 w-5" />Pesan via WhatsApp
              </button>
              {!buyer.name.trim() && <p className="text-center text-[11px] text-[hsl(var(--muted-foreground))]">Isi nama dulu untuk memesan</p>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
